import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, tap } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import { UserSession } from 'app/shared/models';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';
import { loginSuccess } from 'app/state/user-session/user-session.actions';
import { selectLatestSupervisorInHistoryIfDefined } from 'app/state/user-session/user-session.selectors';

import {
    defaultAllowManualSelection,
    defaultAppMode,
    defaultAppSettings,
    defaultInputModeList,
    defaultMasterDataCacheDurationInMs,
    defaultScanFlowRegistrationSubmitIntervalMs,
    defaultUserSessionExpirationTime,
} from '../../models';
import { SettingsStorageService, UserSessionStorageService } from '../../services';
import { preloadSettings, updateAppSettings, updateSettingsSuccess } from './settings.actions';
import { areSettingsLoaded } from './settings.selectors';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class SettingsEffects {
    loadSettings$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(preloadSettings),
            withLatestFrom(this.store.select(areSettingsLoaded)),
            filter(([_, loaded]) => !loaded),
            withLatestFrom(this.store.select(selectLatestSupervisorInHistoryIfDefined)),
            switchMap(([, supervisor]) => {
                const settingsFromStorage = this.settingsService.getAppSettingsFromStorage();
                const currentUserInfo = this.userSessionStorageService.getUserSessionFromStorage();
                const appMode = settingsFromStorage.appMode ?? defaultAppMode;
                const newSettings = {
                    allowManualSelection:
                        settingsFromStorage.allowManualSelection ?? defaultAllowManualSelection,
                    appMode: appMode,
                    inputModes: settingsFromStorage.inputModes ?? defaultInputModeList,
                    userSessionExpirationTime:
                        settingsFromStorage.userSessionExpirationTime ?? defaultUserSessionExpirationTime,
                    masterDataCacheDurationInMs: defaultMasterDataCacheDurationInMs,
                    scanFlowRegistrationSubmitIntervalMs:
                        settingsFromStorage.scanFlowRegistrationSubmitIntervalMs ??
                        defaultScanFlowRegistrationSubmitIntervalMs,
                };

                // If there is not existing userSession stored in the local storage, trigger a login with existing supervisor details
                // This prevents the need for supervisor to login when app is in multi-user mode upon initialisation
                if (appMode === 'multi-user') {
                    if (!currentUserInfo?.userInfo && supervisor) {
                        const supervisorSession: UserSession = {
                            credentials: supervisor.credentials,
                            data: supervisor.data,
                            startedOn: Date.now(),
                            userInfo: supervisor.userInfo,
                        };
                        return [
                            loginSuccess({ userSession: supervisorSession }),
                            updateAppSettings({
                                settings: newSettings,
                            }),
                        ];
                    }
                }

                return [
                    updateAppSettings({
                        settings: newSettings,
                    }),
                ];
            }),
        );
    });

    putAppSettingsToStorage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(updateAppSettings),
                tap(({ settings }) => {
                    this.settingsService.putAppSettingsToStorage(settings);
                }),
            );
        },
        { dispatch: false },
    );

    updateViewAfterAppSettingsUpdated$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(updateSettingsSuccess),
                map(() => globalThis.location.reload()),
            );
        },
        { dispatch: false },
    );

    resetAppSettingsToDefaultValuesOnResetApiConfig$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(resetApiConfig),
            map(() => updateAppSettings({ settings: defaultAppSettings() })),
        );
    });

    constructor(
        private actions$: Actions,
        private settingsService: SettingsStorageService,
        private userSessionStorageService: UserSessionStorageService,
        private store: Store,
    ) {}
}
